/* =============================================================================
   UTILS / ESCPOS-RENDERER — ESC/POS-Codierung für Drucker-Pipeline (Phase 12.1)
   =============================================================================
   Erzeugt aus einer Order und den Restaurant-Stammdaten einen ESC/POS-
   Byte-Stream (kompatibel zu Star CloudPRNT und Epson Connect via Generic-
   ESC/POS-Mode) sowie einen HTML-Fallback (für Mock-Backend und Preview).

   Public API:
     renderReceiptEscpos(order, restaurantMeta, options)
       → { base64: string, html: string, plainText: string }
     renderKitchenTicketEscpos(order, options)
       → { base64: string, html: string, plainText: string }
     renderTestPageEscpos(printer)
       → { base64: string, html: string, plainText: string }

   options:
     - widthMm: 80 (default) | 58
     - encoding: 'cp858' (default) | 'utf8'

   Hinweise:
   - 80mm = 42 Zeichen pro Zeile (Font A), 58mm = 32 Zeichen.
   - Wir nutzen Generic-ESC/POS: ESC @ init, ESC a center/left, ESC E bold,
     GS ! double-size, GS V full cut. Funktioniert auf Star/Epson/Bixolon.
   - Umlaute werden für CP858 ersetzt (ä→0x84, ö→0x94, ü→0x81, Ä→0x8E, ...);
     bei encoding='utf8' werden die Bytes 1:1 weitergegeben.
   - Base64-Encoding via btoa(...) (browser-safe).
============================================================================ */

import { formatCustomizations } from './helpers.js';

// -----------------------------------------------------------------------------
// ESC/POS-Konstanten
// -----------------------------------------------------------------------------
const ESC = 0x1B;
const GS  = 0x1D;
const LF  = 0x0A;

const CMD = {
  init:         [ESC, 0x40],
  alignLeft:    [ESC, 0x61, 0x00],
  alignCenter:  [ESC, 0x61, 0x01],
  alignRight:   [ESC, 0x61, 0x02],
  boldOn:       [ESC, 0x45, 0x01],
  boldOff:      [ESC, 0x45, 0x00],
  underlineOn:  [ESC, 0x2D, 0x01],
  underlineOff: [ESC, 0x2D, 0x00],
  doubleOn:     [GS,  0x21, 0x11],
  doubleOff:    [GS,  0x21, 0x00],
  cutFull:      [GS,  0x56, 0x00],
  cutPartial:   [GS,  0x56, 0x01],
  // Codepage 858 (Multilingual Latin 1 + Euro) — am breitesten verfügbar
  codepage858:  [ESC, 0x74, 0x13]
};

// CP858-Mapping für deutsche Umlaute + €
const CP858_MAP = {
  'ä': 0x84, 'ö': 0x94, 'ü': 0x81,
  'Ä': 0x8E, 'Ö': 0x99, 'Ü': 0x9A,
  'ß': 0xE1, '€': 0xD5,
  'é': 0x82, 'è': 0x8A, 'ê': 0x88,
  'á': 0xA0, 'à': 0x85, 'í': 0xA1, 'ó': 0xA2, 'ú': 0xA3,
  '°': 0xF8, '·': 0xFA, '«': 0xAE, '»': 0xAF,
  '—': 0x2D, // em-dash → -
  '–': 0x2D, // en-dash → -
  '‘': 0x27, '’': 0x27, // smart single quotes → '
  '“': 0x22, '”': 0x22  // smart double quotes → "
};

// -----------------------------------------------------------------------------
// Byte-Buffer
// -----------------------------------------------------------------------------
class EscposBuffer {
  constructor(opts = {}) {
    this.width    = opts.widthMm === 58 ? 32 : 42;
    this.encoding = opts.encoding || 'cp858';
    this.bytes    = [];
    this.plain    = '';
  }
  push(...byteArrays) {
    for (const arr of byteArrays) {
      for (const b of arr) this.bytes.push(b & 0xFF);
    }
    return this;
  }
  cmd(name) { this.push(CMD[name]); return this; }

  text(str) {
    if (!str && str !== 0) str = '';
    str = String(str);
    this.plain += str;
    if (this.encoding === 'utf8') {
      const enc = new TextEncoder().encode(str);
      this.push(Array.from(enc));
    } else {
      for (const ch of str) {
        if (CP858_MAP[ch] !== undefined) {
          this.bytes.push(CP858_MAP[ch]);
        } else {
          const code = ch.charCodeAt(0);
          if (code < 0x80) this.bytes.push(code);
          else this.bytes.push(0x3F); // ?
        }
      }
    }
    return this;
  }
  line(str) { this.text(str || ''); return this.lf(); }
  lf(n = 1) { for (let i = 0; i < n; i++) { this.bytes.push(LF); this.plain += '\n'; } return this; }

  center(str) {
    const padded = padCenter(str, this.width);
    return this.cmd('alignCenter').text(str).lf().cmd('alignLeft')._plainOverride(padded);
  }
  // Bonus-Helper: speichert die zentrierte Variante im plain-text-Mirror,
  // damit Preview hübscher aussieht.
  _plainOverride(padded) {
    // Den letzten Eintrag (= str + \n) im plain ersetzen.
    this.plain = this.plain.slice(0, this.plain.lastIndexOf('\n', this.plain.length - 2) + 1)
               + padded + '\n';
    return this;
  }
  bigCenter(str) {
    // bigCenter im ESC/POS double-size — im Plain-Mirror als zentriert mit
    // mehr "Gewicht" anzeigen (über die Zeile + Spaced-Out-Letters).
    const padded = padCenter(str, this.width);
    this.cmd('alignCenter').cmd('doubleOn').text(str).cmd('doubleOff').lf().cmd('alignLeft');
    this._plainOverride(padded);
    return this;
  }
  boldLine(str) {
    return this.cmd('boldOn').line(str).cmd('boldOff');
  }
  rule(char = '-') {
    return this.line(char.repeat(this.width));
  }
  twoCol(left, right) {
    const ls = String(left || '');
    const rs = String(right || '');
    const space = Math.max(1, this.width - ls.length - rs.length);
    return this.line(ls + ' '.repeat(space) + rs);
  }
  cut() {
    this.lf(3);
    return this.cmd('cutFull');
  }

  /**
   * Cash-Drawer-Pulse (Geldlade öffnen) via ESC/POS `ESC p m t1 t2`.
   * Sequenz: [0x1B, 0x70, m, t1, t2]
   *   - m  = 0 → Pin 2 (Standard für Bondrucker-RJ11), 1 → Pin 5 (alternativ)
   *   - t1 = ON-Zeit in 2ms-Einheiten (default 25 = 50ms)
   *   - t2 = OFF-Zeit in 2ms-Einheiten (default 250 = 500ms)
   *
   * Der Pulse triggert den 24V-Magnet-Kicker in der Geldlade (Cash-Drawer)
   * über den Bondrucker — Standard-Pattern für Kassensysteme. Pitch Slide 8
   * verspricht diese Anbindung; mit dieser Methode ist sie im Code abgedeckt.
   * S57 Block B.
   *
   * Plain-Mirror bekommt einen zentrierten Audit-Marker [KASSE AUF], damit
   * der Bon-Preview im Cockpit sichtbar macht, dass die Lade geöffnet wurde.
   * Im physischen Druck ist der Pulse selbst nicht sichtbar — der Marker
   * erscheint nur im HTML-Fallback.
   *
   * @param {object} [opts] - { pin: 0|1 (default 0), on: 1..255 (default 25),
   *                             off: 1..255 (default 250) }
   */
  openDrawer(opts = {}) {
    const m  = opts.pin === 1 ? 1 : 0;
    const t1 = Math.max(1, Math.min(255, opts.on  ?? 25));
    const t2 = Math.max(1, Math.min(255, opts.off ?? 250));
    this.push([ESC, 0x70, m, t1, t2]);
    this.plain += padCenter('[KASSE AUF]', this.width) + '\n';
    return this;
  }

  /**
   * Druckt einen QR-Code via nativem ESC/POS GS ( k - Befehl (Model 2).
   * Funktioniert auf allen modernen ESC/POS-Druckern inkl. Epson, Star,
   * Bixolon und China-OEMs wie Xprinter / S260M.
   *
   * Sequenz (BSI TR-03153 / KassenSichV QR auf Bondrucker):
   *   1. Model setzen   — Function 165 (cn=49, fn=65) — n1=50 (Model 2), n2=0
   *   2. Modulgröße     — Function 167 (cn=49, fn=67) — n=1..16
   *   3. Fehlerkorr.    — Function 169 (cn=49, fn=69) — 48=L, 49=M, 50=Q, 51=H
   *   4. Daten setzen   — Function 180 (cn=49, fn=80) — m=48, gefolgt von payload
   *   5. Drucken        — Function 181 (cn=49, fn=81) — m=48
   *
   * Plain-Mirror bekommt einen ASCII-Platzhalter, damit der Mock-Backend-
   * HTML-Fallback sichtbar bleibt (Klartext-Payload wird zusätzlich
   * normal als text-line gerendert — Audit-Fallback).
   *
   * @param {string} payload  - Roh-Payload (typ. KassenSichV-QR-String).
   * @param {object} [opts]   - { moduleSize: 1..16 (default 6),
   *                              errorCorrection: 'L'|'M'|'Q'|'H' (default 'M') }
   */
  qrCode(payload, opts = {}) {
    const str = String(payload || '');
    if (!str) return this;
    const moduleSize = Math.max(1, Math.min(16, opts.moduleSize || 6));
    const ecMap = { L: 48, M: 49, Q: 50, H: 51 };
    const ec = ecMap[(opts.errorCorrection || 'M').toUpperCase()] || 49;

    this.cmd('alignCenter');

    // 1. Model setzen (GS ( k <pL=4> <pH=0> <cn=49> <fn=65> <n1=50> <n2=0>)
    this.push([GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00]);
    // 2. Modulgröße (GS ( k 3 0 49 67 n)
    this.push([GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, moduleSize]);
    // 3. Fehlerkorrektur (GS ( k 3 0 49 69 n)
    this.push([GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, ec]);
    // 4. Daten setzen (GS ( k pL pH 49 80 48 d1..dk) — pL/pH = len+3, little-endian
    const dataBytes = (typeof TextEncoder !== 'undefined')
      ? Array.from(new TextEncoder().encode(str))
      : Array.from(str).map(c => c.charCodeAt(0) & 0xFF);
    const totalLen = dataBytes.length + 3;
    const pL = totalLen & 0xFF;
    const pH = (totalLen >> 8) & 0xFF;
    this.push([GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30]);
    this.push(dataBytes);
    // 5. Drucken (GS ( k 3 0 49 81 48)
    this.push([GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30]);

    this.cmd('alignLeft');

    // Plain-Mirror: zentrierter [QR-CODE]-Platzhalter — die Klartext-Payload
    // selbst wird vom Caller als eigene Zeile ausgegeben (Audit-Fallback).
    this.plain += padCenter('[QR-CODE]', this.width) + '\n';
    return this;
  }

  toBase64() {
    return bytesToBase64(this.bytes);
  }
  toPlainText() {
    return this.plain;
  }
}

// -----------------------------------------------------------------------------
// Helper
// -----------------------------------------------------------------------------
function padCenter(str, width) {
  const s = String(str || '');
  if (s.length >= width) return s.slice(0, width);
  const pad = Math.floor((width - s.length) / 2);
  return ' '.repeat(pad) + s;
}

function bytesToBase64(bytes) {
  // Browser-safe: bytes-Array → binary string → btoa
  // (für lange Strings chunked, um Stack-Overflow zu vermeiden)
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    bin += String.fromCharCode.apply(null, slice);
  }
  // btoa nimmt nur ASCII — und unsere bytes sind 0..255 → ok über char-codes.
  if (typeof btoa === 'function') return btoa(bin);
  // Node-Fallback (Tests)
  if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64');
  throw new Error('No base64 encoder available');
}

function formatCents(cents) {
  const sign = cents < 0 ? '-' : '';
  const abs  = Math.abs(cents);
  const eur  = Math.floor(abs / 100);
  const ct   = String(abs % 100).padStart(2, '0');
  return `${sign}${eur},${ct} €`;
}

function nowStr() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth()+1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getOrderItems(order) {
  return Array.isArray(order && order.items) ? order.items
       : Array.isArray(order && order.preorderSnapshot && order.preorderSnapshot.items) ? order.preorderSnapshot.items
       : [];
}

function getOrderTax(order) {
  // Mapper liefert subtotalNetCents/totalGrossCents — wir lesen defensiv
  // beide Varianten + snake_case-Rohfelder.
  return {
    net:  order.subtotalNetCents ?? order.netCents     ?? order.subtotal_net_cents ?? order.net_cents ?? 0,
    t7:   order.tax7Cents        ?? order.tax7AmountCents ?? order.tax_7_amount_cents ?? order.tax_7_cents ?? 0,
    t19:  order.tax19Cents       ?? order.tax19AmountCents ?? order.tax_19_amount_cents ?? order.tax_19_cents ?? 0,
    gross: order.totalGrossCents ?? order.grossCents   ?? order.totalCents ?? order.total_gross_cents ?? order.gross_cents ?? order.total_cents ?? 0
  };
}

/** String in mehrere Zeilen <width> umbrechen (word-aware). */
function wrapText(str, width) {
  const s = String(str || '').trim();
  if (s.length <= width) return [s];
  const words = s.split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if ((cur + ' ' + w).trim().length <= width) {
      cur = (cur + ' ' + w).trim();
    } else {
      if (cur) lines.push(cur);
      // Falls ein einzelnes Wort länger als width ist, hart cutten.
      if (w.length > width) {
        for (let i = 0; i < w.length; i += width) lines.push(w.slice(i, i + width));
        cur = '';
      } else {
        cur = w;
      }
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

function getPaymentLabel(method) {
  const map = { cash: 'Barzahlung', card: 'Kartenzahlung', stripe: 'Online (Stripe)', refunded: 'Erstattet' };
  return map[method] || method || '—';
}

// -----------------------------------------------------------------------------
// Render-Funktionen
// -----------------------------------------------------------------------------

/**
 * Kassenbon (Quittungsdrucker, Theke) — finanzamt-konform.
 */
export function renderReceiptEscpos(order, restaurantMeta = {}, options = {}) {
  const buf  = new EscposBuffer(options);
  const meta = restaurantMeta || {};
  const tax  = getOrderTax(order);
  const items = getOrderItems(order);
  const isCorrection = !!(order && (order.isCorrection || order.is_correction));

  buf.cmd('init').cmd('codepage858');

  // Header
  buf.bigCenter(meta.legalName || meta.name || 'Grill & Green');
  if (meta.street)      buf.center([meta.street].filter(Boolean).join(' '));
  if (meta.zip || meta.city) buf.center([meta.zip, meta.city].filter(Boolean).join(' '));
  if (meta.phone)       buf.center('Tel. ' + meta.phone);
  if (meta.ustId)       buf.center('USt-IdNr ' + meta.ustId);
  if (meta.taxNumber)   buf.center('Steuernr. ' + meta.taxNumber);
  buf.lf();
  buf.rule('=');

  // Belegtyp
  if (isCorrection) {
    buf.bigCenter('STORNOBELEG');
    if (order.correctsOrderId || order.corrects_order_id) {
      buf.center('zu Beleg-Nr. ' + (order.correctsOrderId || order.corrects_order_id));
    }
  } else {
    buf.bigCenter('KASSENBELEG');
  }
  buf.lf();

  // Belegnummer + Datum
  buf.twoCol('Beleg-Nr.', String(order.invoiceNumber || order.invoice_number || '—'));
  buf.twoCol('Bestell-Nr.', String(order.orderNumber || order.order_number || '—'));
  buf.twoCol('Datum/Uhrzeit', order.paidAtFormatted || nowStr());
  if (order.pickupTime || order.pickup_time) {
    buf.twoCol('Abholzeit', String(order.pickupTime || order.pickup_time));
  }
  buf.rule('-');

  // Positionen
  for (const it of items) {
    const qty   = Number(it.quantity || it.qty || 1);
    const name  = String(it.name || it.nameSnapshot || it.name_snapshot || it.title || 'Position');
    const line  = (it.lineTotalCents ?? it.line_total_cents ?? (it.unitPriceCents || 0) * qty);
    buf.twoCol(`${qty}x ${name}`.slice(0, buf.width - 12), formatCents(line));
    const cust = it.customizations || it.customization;
    if (cust && Object.keys(cust).length > 0 && typeof formatCustomizations === 'function') {
      const parts = formatCustomizations(cust) || [];
      // Jede Anpassung als eigene Zeile, eingerückt, word-wrap auf Restbreite.
      for (const part of parts) {
        const wrapped = wrapText(part, buf.width - 3);
        for (let i = 0; i < wrapped.length; i++) {
          buf.line((i === 0 ? '   • ' : '     ') + wrapped[i]);
        }
      }
    }
  }
  if (items.length === 0) {
    buf.line('(keine Positionen)');
  }
  buf.rule('-');

  // Summen
  buf.twoCol('Netto',      formatCents(tax.net));
  if (tax.t7)  buf.twoCol('MwSt 7%',  formatCents(tax.t7));
  if (tax.t19) buf.twoCol('MwSt 19%', formatCents(tax.t19));
  buf.cmd('boldOn');
  buf.twoCol('GESAMT', formatCents(tax.gross));
  buf.cmd('boldOff');
  buf.lf();

  // Bezahlart
  const method = order.paymentMethod || order.payment_method;
  buf.twoCol('Bezahlt', getPaymentLabel(method));
  buf.lf();

  // TSE-Block
  buf.rule('-');
  buf.boldLine('TSE-Signatur');
  if (order.tseSignedAt || order.tse_signed_at) {
    buf.line('Sig.-Zähler: ' + (order.tseSignatureCounter || order.tse_signature_counter || '—'));
    buf.line('Tx-Nr:       ' + (order.tseTransactionId || order.tse_transaction_id || '—'));
    buf.line('Sig:         ' + String(order.tseSignatureValue || order.tse_signature_value || '—').slice(0, buf.width - 13));
    buf.line('Signiert am: ' + (order.tseSignedAt || order.tse_signed_at));

    // S55 Block O — nativer ESC/POS-QR auf Bondrucker (BSI TR-03153 Payload).
    // Pflicht-Schritt für KassenSichV-konformen gedruckten Bon. Klartext-Payload
    // bleibt als Fallback unter dem QR — falls der Drucker den GS ( k -
    // Befehl nicht unterstützt, ist der Bon trotzdem komplett auditierbar.
    const qrPayload = order.tseQrPayload || order.tse_qr_payload || '';
    if (qrPayload) {
      buf.lf();
      buf.qrCode(qrPayload, { moduleSize: 6, errorCorrection: 'M' });
      buf.lf();
      // Klartext-Payload zusätzlich als Audit-Fallback unten drunter, in
      // schmaler Schrift (per word-wrap auf Bonbreite).
      const wrapped = wrapText(qrPayload, buf.width);
      for (const w of wrapped) buf.line(w);
    }
  } else {
    buf.line('Signatur in Vorbereitung (TSE-Mode disabled)');
  }
  buf.rule('-');

  // Footer
  buf.lf();
  buf.center('Vielen Dank für Ihren Einkauf!');
  buf.center(meta.name || 'Grill & Green');
  buf.lf();
  // Brand-Signatur Kassensystem (S56 Block 0.3)
  buf.center('Kassensystem: YOUR GROWTH 365');
  buf.lf();

  // S57 Block B — Cash-Drawer-Pulse vor cut() ausgelöst, sobald Bar bezahlt
  // wurde und keine Stornierung vorliegt. ESC p triggert den 24V-Kicker
  // im Bondrucker → Geldlade öffnet sich automatisch. Pitch-Truth-Befund
  // aus S56 (Slide 8) damit im Code abgedeckt.
  const _payMethod = order.paymentMethod || order.payment_method;
  if (_payMethod === 'cash' && !isCorrection) {
    buf.openDrawer();
  }

  buf.cut();

  return {
    base64:    buf.toBase64(),
    plainText: buf.toPlainText(),
    html:      plainToHtml(buf.toPlainText(), { widthMm: options.widthMm || 80 })
  };
}

/**
 * Küchen-Bon — keine Preise, große Schrift, Items mit Anpassungen.
 */
export function renderKitchenTicketEscpos(order, options = {}) {
  const buf = new EscposBuffer(options);
  const items = getOrderItems(order);

  buf.cmd('init').cmd('codepage858');

  buf.bigCenter('KÜCHE');
  buf.lf();
  buf.twoCol('Bestell-Nr.', String(order.orderNumber || order.order_number || '—'));
  buf.twoCol('Eingang',     nowStr());
  if (order.pickupTime || order.pickup_time) {
    buf.cmd('boldOn').twoCol('Abholzeit', String(order.pickupTime || order.pickup_time)).cmd('boldOff');
  }
  if (order.customerName || order.customer_name) {
    buf.twoCol('Kunde', String(order.customerName || order.customer_name));
  }
  buf.rule('=');

  for (const it of items) {
    const qty  = Number(it.quantity || it.qty || 1);
    const name = String(it.name || it.nameSnapshot || it.name_snapshot || it.title || 'Position');
    buf.cmd('boldOn').cmd('doubleOn').line(`${qty}x ${name}`).cmd('doubleOff').cmd('boldOff');
    const cust = it.customizations || it.customization;
    if (cust && Object.keys(cust).length > 0 && typeof formatCustomizations === 'function') {
      const parts = formatCustomizations(cust) || [];
      for (const part of parts) {
        const wrapped = wrapText(part, buf.width - 4);
        for (let i = 0; i < wrapped.length; i++) {
          buf.line((i === 0 ? '  • ' : '    ') + wrapped[i]);
        }
      }
    }
    if (it.notes || it.special_requests) {
      buf.line('  ' + String(it.notes || it.special_requests).slice(0, buf.width - 2));
    }
    buf.lf();
  }
  if (items.length === 0) {
    buf.line('(keine Positionen)');
  }

  if (order.specialRequests || order.special_requests) {
    buf.rule('-');
    buf.boldLine('Sonderwünsche:');
    buf.line(String(order.specialRequests || order.special_requests));
  }

  buf.cut();

  return {
    base64:    buf.toBase64(),
    plainText: buf.toPlainText(),
    html:      plainToHtml(buf.toPlainText(), { widthMm: options.widthMm || 80, title: 'Küchen-Bon' })
  };
}

/**
 * S63 Block S4 — Teilstorno-Korrekturbeleg als nativer ESC/POS-Druck.
 *
 * Im Gegensatz zu renderReceiptEscpos (das mit einer Pseudo-Order isCorrection=true
 * arbeitet und ALLE Items der Pseudo-Order druckt) baut dieser Renderer einen
 * dedizierten Teilstorno-Beleg, der nur die wirklich stornierten Positionen
 * mit ihren Korrektur-IDs aufführt. Bedienung GoBD §146a AO + §147 AO.
 *
 * @param {object} originalOrder    Header-Daten der Original-Order (Beleg-Nr, Datum)
 * @param {Array<{correction, item}>} createdCorrections   Aus submitPartialCorrectionDialog
 * @param {string} reasonText       Aggregierter Storno-Grund
 * @param {object|null} sig         TSE-Signatur-Block aus tseSignAndRecord (oder null = pending)
 * @param {object} restaurantMeta   Restaurant-Stammdaten (legalName, street, USt)
 * @param {object} options          { widthMm }
 */
export function renderPartialCorrectionEscpos(originalOrder, createdCorrections, reasonText, sig, restaurantMeta = {}, options = {}) {
  const buf  = new EscposBuffer(options);
  const meta = restaurantMeta || {};
  const totalCents = (createdCorrections || []).reduce(
    (s, c) => s + (c.item.unitPriceCents || 0) * (c.item.qtyToCancel || 0), 0
  );
  const tax7Cents = (createdCorrections || [])
    .filter(c => c.item.taxRate === 7)
    .reduce((s, c) => s + (c.item.unitPriceCents || 0) * (c.item.qtyToCancel || 0) * 7  / 107, 0);
  const tax19Cents = (createdCorrections || [])
    .filter(c => c.item.taxRate === 19)
    .reduce((s, c) => s + (c.item.unitPriceCents || 0) * (c.item.qtyToCancel || 0) * 19 / 119, 0);
  const netCents = totalCents - Math.round(tax7Cents) - Math.round(tax19Cents);

  buf.cmd('init').cmd('codepage858');

  // Header
  buf.bigCenter(meta.legalName || meta.name || 'Grill & Green');
  if (meta.street)      buf.center([meta.street].filter(Boolean).join(' '));
  if (meta.zip || meta.city) buf.center([meta.zip, meta.city].filter(Boolean).join(' '));
  if (meta.ustId)       buf.center('USt-IdNr ' + meta.ustId);
  if (meta.taxNumber)   buf.center('Steuernr. ' + meta.taxNumber);
  buf.lf();
  buf.rule('=');

  // Belegtyp
  buf.bigCenter('KORREKTURBELEG');
  buf.center('TEIL-STORNO  §146a AO');
  buf.lf();

  // Referenz auf Original
  buf.twoCol('Original-Rg.', String(originalOrder.invoiceNumber || originalOrder.invoice_number || originalOrder.orderNumber || '—'));
  buf.twoCol('Original-Bon-Nr.', String(originalOrder.orderNumber || originalOrder.order_number || '—'));
  buf.twoCol('Storno-Datum', nowStr());
  buf.rule('-');

  // Positionen
  for (const { item, correction } of createdCorrections) {
    const qty   = Number(item.qtyToCancel || 0);
    const name  = String(item.name || 'Position');
    const lineCents = (item.unitPriceCents || 0) * qty;
    // Negativ-Betrag für Storno-Sicht
    buf.twoCol(`-${qty}x ${name}`.slice(0, buf.width - 12), '-' + formatCents(lineCents));
    // Korrektur-ID-Suffix als Audit-Marker (letzte 8 Zeichen)
    const corrId = String(correction.id || correction.correction_id || '').slice(-8);
    if (corrId) buf.line('   Korr-ID: ' + corrId);
  }
  buf.rule('-');

  // Summen (alle negativ — Erstattung)
  buf.twoCol('Netto',   '-' + formatCents(netCents));
  if (Math.round(tax7Cents))  buf.twoCol('MwSt 7%',  '-' + formatCents(Math.round(tax7Cents)));
  if (Math.round(tax19Cents)) buf.twoCol('MwSt 19%', '-' + formatCents(Math.round(tax19Cents)));
  buf.cmd('boldOn');
  buf.twoCol('ERSTATTUNG', '-' + formatCents(totalCents));
  buf.cmd('boldOff');
  buf.lf();

  // Storno-Grund
  buf.rule('-');
  buf.boldLine('Grund:');
  const reasonLines = wrapText(reasonText || '—', buf.width);
  for (const line of reasonLines) buf.line(line);
  buf.lf();

  // TSE-Storno-Signatur
  buf.rule('-');
  buf.boldLine('TSE-Storno-Signatur');
  if (sig && sig.txNumber) {
    buf.line('Sig.-Zähler: ' + (sig.signatureCounter || '—'));
    buf.line('Tx-Nr:       ' + sig.txNumber);
    buf.line('Modus:       ' + (sig.mode || 'fiskaly'));
    if (sig.qrPayload) {
      buf.lf();
      buf.qrCode(sig.qrPayload, { moduleSize: 6, errorCorrection: 'M' });
      buf.lf();
      const wrapped = wrapText(sig.qrPayload, buf.width);
      for (const w of wrapped) buf.line(w);
    }
  } else {
    buf.line('⚠ Pending — wird via Reconciliation-Job ergänzt');
  }
  buf.rule('-');

  // Footer
  buf.lf();
  buf.center('Aufbewahrung §147 AO — 10 Jahre');
  buf.center('GoBD-konform Append-Only');
  buf.lf();
  buf.center('Kassensystem: YOUR GROWTH 365');
  buf.lf();

  buf.cut();

  return {
    base64:    buf.toBase64(),
    plainText: buf.toPlainText(),
    html:      plainToHtml(buf.toPlainText(), { widthMm: options.widthMm || 80, title: 'Korrekturbeleg' })
  };
}

/**
 * Testseite — wird beim "Testdruck"-Button im Settings-Tab gerendert.
 */
export function renderTestPageEscpos(printer = {}, options = {}) {
  const buf = new EscposBuffer({ widthMm: printer.paperWidthMm || options.widthMm || 80, ...options });
  buf.cmd('init').cmd('codepage858');
  buf.bigCenter('TESTDRUCK');
  buf.lf();
  buf.center('Grill & Green');
  buf.center(`Drucker: ${printer.name || '—'}`);
  buf.center(`Typ:     ${printer.kind || '—'}`);
  buf.center(`Backend: ${printer.backend || '—'}`);
  buf.center(`Breite:  ${printer.paperWidthMm || 80} mm`);
  buf.center(`Zeit:    ${nowStr()}`);
  buf.lf();
  buf.rule('=');
  buf.center('Wenn Du das lesen kannst,');
  buf.center('ist die Verbindung in Ordnung.');
  buf.rule('=');
  buf.lf();
  buf.center('Umlaut-Test: ä ö ü ß Ä Ö Ü € °');
  buf.lf();
  buf.cut();

  return {
    base64:    buf.toBase64(),
    plainText: buf.toPlainText(),
    html:      plainToHtml(buf.toPlainText(), { widthMm: printer.paperWidthMm || 80, title: 'Testdruck' })
  };
}

// -----------------------------------------------------------------------------
// HTML-Fallback für Mock-Backend (Preview im Browser)
// -----------------------------------------------------------------------------
function plainToHtml(plain, opts = {}) {
  const widthMm = opts.widthMm || 80;
  const widthPx = widthMm === 58 ? 240 : 340;
  const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<pre style="
      font-family: 'Courier New', 'Lucida Console', monospace;
      font-size: 12px;
      line-height: 1.35;
      white-space: pre;
      background: #fff;
      color: #111;
      width: ${widthPx}px;
      margin: 0 auto;
      padding: 12px;
      border: 1px dashed #bbb;
      border-radius: 6px;">${esc(plain)}</pre>`;
}

// -----------------------------------------------------------------------------
// Backward-Compat (für Inline-Usage aus index.html)
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderReceiptEscpos,
    renderKitchenTicketEscpos,
    renderTestPageEscpos
  });
}
